import { Injectable } from '@angular/core';
import { BonusStage } from './user/data/user-bonuses.data';

@Injectable({
  providedIn: 'root'
})
export class ArrayService {

  constructor() {
  }

  /**
   * Split array to chunks
   *
   * Example:
   *  toChunks([1, 2, 3, 4, 5, 6, 7], 3)
   *
   * Output:
   *  [[1, 2, 3], [4, 5, 6], [7]]
   *
   * @param original
   * @param chunkSize
   */
  toChunks(original: Array<any>, chunkSize: number) {
    const result = [];

    for (let i = 0; i < original.length; i += chunkSize) {
      result.push(original.slice(i, i + chunkSize));
    }

    return result;
  }

  /**
   * Cycle array items
   *
   * @param {any[]} originalArray
   * @param {number} count
   * @returns {any[]}
   * @private
   */
  cycle(originalArray: any[], count: number) {

    const newArray = [];

    if (!originalArray || !originalArray.length) {
      return newArray;
    }

    while (newArray.length < count) {
      for (const arr of originalArray) {
        if (newArray.length < count) {
          newArray.push(arr);
        }
      }
    }

    return newArray;
  }

  /**
   * Returns array from object values
   *
   * Example:
   *  fromObjectValues({
   *    error: {
   *      amount: 'not enough funds'
   *    }
   *  })
   *
   * Output:
   *  ['not enough funds']
   *
   * @param object
   */
  fromObjectValues(object: object): any[] {
    const list = [];

    Object.values(object).forEach(
      value => typeof value === 'object'
        ? list.push(...this.fromObjectValues(value))
        : list.push(value)
    );

    return list;
  }

  /**
   * Get unique elements by key
   *
   * @param {string} key
   * @param {any[]} arr
   * @returns {any}
   */
  uniqueElements(key: string, arr: any[]) {
    const sort = arr.reduce((acc, cur) => {
      /**
       * Temporary condition for deposit bonus need improve in future
       */
      if (cur.bonus) {
        acc[cur.bonus[key]] = {...acc[cur.bonus[key]], ...cur};
      } else if (cur.stage && cur.stage !== BonusStage.EXPIRED && cur.stage !== BonusStage.FINISHED) {
        acc[cur[key]] = {...acc[cur[key]], ...cur};
      }
      return acc;
    }, {});

    return Object.values(sort);
  }

  /**
   * Returns array of url segments from string
   *
   * @param route
   * @private
   */
  routeToArray(route: string): Array<string> {
    const url = route.split('?')[0];

    return (url.split('/')).splice(1);
  }

  /**
   * Check if array has min length of game and clone it
   * @param gameList
   * @param slideCount
   * @private
   */
  public checkAndCloneArray(gameList: any, slideCount: number) {

    let cloneArr = [];

    const arrayToChunks = this.toChunks(gameList, slideCount);

    if (gameList.length % slideCount === 0) {
      cloneArr = gameList;
    } else {
      cloneArr = this.cycle(gameList, gameList.length + slideCount - arrayToChunks[arrayToChunks.length - 1].length);
    }

    return cloneArr;
  }

  /**
   * Returns array of unique object values from array of objects by key
   *
   * Example:
   *  uniqueBy('id', [
   *    {id: 21, amount: 1},
   *    {id: 21, amount: 2},
   *    {id: 4, amount: 5},
   *  ])
   *
   * Output:
   *  [
   *    {id: 21, amount: 1},
   *    {id: 4, amount: 5},
   *  ]
   *
   * @param key
   */
  public uniqueBy(prop: string, arr: any[]){
    return [...new Map(arr.map(item => [item[prop], item])).values()];
  }

  /**
   * Shuffle array
   * @param array
   */
  shuffle(array: any[]) {
    let currentIndex = array.length;
    let temporaryValue;
    let randomIndex;
    while (0 !== currentIndex) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
    return array;
  }

  /**
   * Sort array of objects based on another array
   */
  public mapOrder(array, order, key) {
    array.sort(function (a, b) {
      const A = a[key], B = b[key];
      if (order.indexOf(A) > order.indexOf(B)) {
        return 1;
      } else {
        return -1;
      }
    });
    return array;
  }

  /**
   * Checks if two objects are deeply equal by comparing their
   * properties recursively.
   * @param obj1
   * @param obj2
   * @returns boolean
   */
  isEqual(obj1, obj2) {
    if (obj1 === obj2) {
        return true;
    }

    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key) || !this.isEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
  }
}
